import {
  type CompanyDto,
  type EmployeeProfileDto,
  employeeProfileSchema,
  EMPLOYMENT_TYPES,
  type EmploymentType,
  type MeDto,
  QUALIFICATION_KINDS,
  type QualificationDto,
  type QualificationKind,
  qualificationState,
  type RoleDto,
  datePl,
  suggestLogin,
  todayInWarsaw,
  type UserDto,
} from '@bqcrm/shared';
import { ActionIcon, Alert, Badge, Button, Card, Checkbox, Grid, Group, MultiSelect, PasswordInput, Select, SimpleGrid, Stack, Switch, Table, Tabs, Text, Textarea, TextInput } from '@mantine/core';
import { IconCertificate, IconFingerprint, IconPlus, IconShieldCheck, IconTrash, IconUserQuestion } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { ask } from '../../confirm';
import { MobileDevices } from '../../components/MobileDevices';
import { AbsenceLadderTab } from './AbsenceLadderTab';
import { PageHeader } from '../../components/PageHeader';
import { AccessTab } from './AccessTab';
import { userBody } from './userBody';
import { QueryState } from '../../components/QueryState';
import { EmptyState, SectionCard } from '../../components/ui';
import { useTrades } from '../../data';
import { formatDateTime, formatPostalCode } from '../../format';
import { usePostalCity } from '../../postalCity';
import { useSaveShortcut } from '../../hooks';
import { notifyError, notifyOk } from '../../notify';

/** Szerokość treści zakładek karty pracownika. */
const MAW = 900;

/** Treść tylko do odczytu: wyłączone pola i przyciski (natywny `fieldset disabled`). */
function ReadOnly({ on, children }: { on: boolean; children: ReactNode }) {
  if (!on) return children;
  return (
    <fieldset disabled style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      {children}
    </fieldset>
  );
}

/** Zgłasza stronie, czy zakładka ma niezapisane zmiany (przy odmontowaniu — brak zmian). */
function useReportDirty(dirty: boolean, onDirty: (d: boolean) => void) {
  useEffect(() => onDirty(dirty), [dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onDirty(false), []); // eslint-disable-line react-hooks/exhaustive-deps
}


function useRefresh(id: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['users'] });
    void qc.invalidateQueries({ queryKey: ['employee-profile', id] });
    void qc.invalidateQueries({ queryKey: ['users-lite'] });
  };
}

// ── dane pracownika ────────────────────────────────────────────────────────

function DataTab({ user, profile, me, onDirty }: { user: UserDto; profile: EmployeeProfileDto; me: MeDto; onDirty: (d: boolean) => void }) {
  const refresh = useRefresh(user.id);
  const trades = useTrades();
  const init = () => ({
    fullName: user.fullName,
    phone: user.phone,
    email: user.email ?? '',
    isSubcontractor: user.isSubcontractor,
    active: user.active,
    p: { ...profile },
  });
  const [f, setF] = useState(init);
  useEffect(() => setF(init()), [user, profile]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (p: Partial<ReturnType<typeof init>>) => setF((x) => ({ ...x, ...p }));
  const setP = (p: Partial<EmployeeProfileDto>) => setF((x) => ({ ...x, p: { ...x.p, ...p } }));
  const cityHint = usePostalCity(f.p.postalCode, f.p.city, (city) => setP({ city }));
  const save = useMutation({
    mutationFn: async () => {
      const prof = employeeProfileSchema.safeParse(f.p);
      if (!prof.success) throw new Error(prof.error.issues.map((i) => i.message).join('; '));
      await api(`/users/${user.id}`, { method: 'PUT', body: userBody(user, { fullName: f.fullName, phone: f.phone, email: f.email, isSubcontractor: f.isSubcontractor, active: f.active }) });
      await api(`/users/${user.id}/profile`, { method: 'PUT', body: prof.data });
    },
    onSuccess: () => {
      notifyOk('Zapisano dane pracownika');
      refresh();
    },
    onError: notifyError,
  });
  const self = me.id === user.id;
  const dirty = JSON.stringify(f) !== JSON.stringify(init());
  useReportDirty(dirty, onDirty);
  useSaveShortcut(dirty && !save.isPending, () => save.mutate());
  return (
    <Stack maw={MAW}>
      <SectionCard title="Podstawowe">
        <Stack gap="sm">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput label="Imię i nazwisko" value={f.fullName} onChange={(e) => set({ fullName: e.currentTarget.value })} required />
            <TextInput label="Stanowisko" placeholder="np. monter instalacji PV" value={f.p.position} onChange={(e) => setP({ position: e.currentTarget.value })} />
            <Select
              label="Forma współpracy"
              data={Object.entries(EMPLOYMENT_TYPES).map(([value, label]) => ({ value, label }))}
              value={f.p.employmentType}
              onChange={(v) => setP({ employmentType: (v as EmploymentType) ?? null })}
              clearable
            />
            <TextInput type="date" label="Data rozpoczęcia pracy" value={f.p.hiredAt ?? ''} onChange={(e) => setP({ hiredAt: e.currentTarget.value || null })} />
          </SimpleGrid>

          <Group gap="xl">
            <Switch label="Podwykonawca" checked={f.isSubcontractor} onChange={(e) => set({ isSubcontractor: e.currentTarget.checked })} />
            <Switch
              label="Pracownik aktywny"
              description="Nieaktywny znika z list wyboru; ma też zablokowane konto."
              checked={f.active}
              disabled={self}
              onChange={(e) => set({ active: e.currentTarget.checked })}
            />
          </Group>
          <div>
            <Text size="sm" fw={500}>
              Branże (planowanie osób spoza ekip)
            </Text>
            <Text size="sm" c={user.tradeCodes.length ? undefined : 'dimmed'}>
              {user.tradeCodes.length ? user.tradeCodes.map((c) => trades.data?.find((t) => t.code === c)?.name ?? c).join(', ') : '—'}
            </Text>
            <Text size="xs" c="dimmed">
              Zmienia się je w zakładce „Dostęp” → „Praca w terenie — branże”. Gdy osoba jest w ekipie, decydują branże ekipy.
            </Text>
          </div>
        </Stack>
      </SectionCard>
      <SectionCard title="Kontakt i adres">
        <Stack gap="sm">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput label="Telefon służbowy" value={f.phone} onChange={(e) => set({ phone: e.currentTarget.value })} />
            <TextInput label="Telefon prywatny" value={f.p.privatePhone} onChange={(e) => setP({ privatePhone: e.currentTarget.value })} />
            <TextInput label="E-mail" description={user.hasAccount ? 'Można też nim logować się zamiast loginu' : undefined} value={f.email} onChange={(e) => set({ email: e.currentTarget.value })} />
          </SimpleGrid>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput label="Ulica i numer" value={f.p.street} onChange={(e) => setP({ street: e.currentTarget.value })} />
            </Grid.Col>
            <Grid.Col span={{ base: 4, sm: 2 }}>
              <TextInput label="Kod" placeholder="00-000" inputMode="numeric" maxLength={6} value={f.p.postalCode} onChange={(e) => setP({ postalCode: formatPostalCode(e.currentTarget.value) })} />
            </Grid.Col>
            <Grid.Col span={{ base: 8, sm: 4 }}>
              <TextInput label="Miejscowość" value={f.p.city} description={cityHint} onChange={(e) => setP({ city: e.currentTarget.value })} />
            </Grid.Col>
          </Grid>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput label="Kontakt w nagłych wypadkach" placeholder="imię i nazwisko, kim jest" value={f.p.emergencyName} onChange={(e) => setP({ emergencyName: e.currentTarget.value })} />
            <TextInput label="Telefon kontaktu awaryjnego" value={f.p.emergencyPhone} onChange={(e) => setP({ emergencyPhone: e.currentTarget.value })} />
          </SimpleGrid>
        </Stack>
      </SectionCard>
      <SectionCard title="Praca w terenie">
        <Stack gap="sm">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput label="Prawo jazdy (kategorie)" placeholder="np. B, C" value={f.p.drivingLicence} onChange={(e) => setP({ drivingLicence: e.currentTarget.value })} />
            <TextInput label="Samochód służbowy (nr rejestracyjny)" value={f.p.vehiclePlate} onChange={(e) => setP({ vehiclePlate: e.currentTarget.value.toUpperCase() })} />
          </SimpleGrid>
          <Textarea label="Uwagi" autosize minRows={2} value={f.p.notes} onChange={(e) => setP({ notes: e.currentTarget.value })} />
        </Stack>
      </SectionCard>
      <Group justify="space-between">
        <Text size="xs" c="dimmed">
          {profile.updatedAt ? `Karta zmieniona: ${formatDateTime(profile.updatedAt)}` : ''}
        </Text>
        <Button onClick={() => save.mutate()} loading={save.isPending}>
          Zapisz
        </Button>
      </Group>
    </Stack>
  );
}

// ── uprawnienia zawodowe ───────────────────────────────────────────────────

export function QualificationBadge({ q }: { q: QualificationDto }) {
  const st = qualificationState(q.validUntil, todayInWarsaw());
  if (st === 'ok') return q.validUntil ? <Text size="sm">do {datePl(q.validUntil)}</Text> : <Text size="sm" c="dimmed">bezterminowo</Text>;
  return (
    <Badge color={st === 'niewazne' ? 'red' : 'orange'} variant="light">
      {st === 'niewazne' ? `nieważne od ${datePl(q.validUntil!)}` : `wygasa ${datePl(q.validUntil!)}`}
    </Badge>
  );
}

function QualificationsTab({ user, profile, onDirty }: { user: UserDto; profile: EmployeeProfileDto; onDirty: (d: boolean) => void }) {
  const refresh = useRefresh(user.id);
  const [rows, setRows] = useState<QualificationDto[]>(profile.qualifications);
  useEffect(() => setRows(profile.qualifications), [profile]);
  const setRow = (i: number, p: Partial<QualificationDto>) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const save = useMutation({
    mutationFn: () => api(`/users/${user.id}/profile`, { method: 'PUT', body: { ...profile, qualifications: rows } }),
    onSuccess: () => {
      notifyOk('Zapisano uprawnienia zawodowe');
      refresh();
    },
    onError: notifyError,
  });
  const dirty = JSON.stringify(rows) !== JSON.stringify(profile.qualifications);
  useReportDirty(dirty, onDirty);
  useSaveShortcut(dirty && !save.isPending, () => save.mutate());
  return (
    <Stack maw={MAW}>
      <Text size="sm" c="dimmed">
        Uprawnienia i badania potrzebne do pracy w branżach — np. certyfikat F-gazowy (klimatyzacja, pompy ciepła), SEP (elektryka, fotowoltaika), badania lekarskie, BHP. Termin wygasający w ciągu 30 dni jest oznaczony.
      </Text>
      <Card withBorder padding={0}>
        <Table.ScrollContainer minWidth={700}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={210}>Rodzaj</Table.Th>
                <Table.Th>Opis / nazwa</Table.Th>
                <Table.Th w={150}>Numer</Table.Th>
                <Table.Th w={150}>Ważne do</Table.Th>
                <Table.Th w={150}>Stan</Table.Th>
                <Table.Th w={40} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((q, i) => (
                <Table.Tr key={i}>
                  <Table.Td>
                    <Select size="xs" data={Object.entries(QUALIFICATION_KINDS).map(([value, label]) => ({ value, label }))} value={q.kind} allowDeselect={false} onChange={(v) => v && setRow(i, { kind: v as QualificationKind })} />
                  </Table.Td>
                  <Table.Td>
                    <TextInput size="xs" placeholder={q.kind === 'inne' ? 'nazwa uprawnienia' : 'np. kategoria, zakres'} value={q.name} onChange={(e) => setRow(i, { name: e.currentTarget.value })} />
                  </Table.Td>
                  <Table.Td>
                    <TextInput size="xs" value={q.number} onChange={(e) => setRow(i, { number: e.currentTarget.value })} />
                  </Table.Td>
                  <Table.Td>
                    <TextInput size="xs" type="date" value={q.validUntil ?? ''} onChange={(e) => setRow(i, { validUntil: e.currentTarget.value || null })} />
                  </Table.Td>
                  <Table.Td>
                    <QualificationBadge q={q} />
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon variant="subtle" color="red" aria-label="Usuń" onClick={() => setRows((r) => r.filter((_, j) => j !== i))}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
              {rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <EmptyState compact icon={<IconCertificate size={18} />} title="Brak uprawnień zawodowych" hint="Dodaj uprawnienie albo badanie z terminem ważności." />
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
      <Group justify="space-between">
        <Button variant="light" leftSection={<IconPlus size={14} />} onClick={() => setRows((r) => [...r, { kind: 'sep_e', name: '', number: '', validUntil: null }])}>
          Dodaj uprawnienie
        </Button>
        <Button onClick={() => save.mutate()} loading={save.isPending}>
          Zapisz
        </Button>
      </Group>
    </Stack>
  );
}

// ── konto użytkownika ──────────────────────────────────────────────────────

function SecurityCard({ user }: { user: UserDto }) {
  const refresh = useRefresh(user.id);
  const [password, setPassword] = useState('');
  const [requireChange, setRequireChange] = useState(true);
  const required = useMutation({
    mutationFn: (on: boolean) => api(`/users/${user.id}/password-change-required`, { method: 'PUT', body: { required: on } }),
    onSuccess: (_, on) => (notifyOk(on ? 'Pracownik zmieni hasło przy kolejnym logowaniu' : 'Wyłączono wymaganą zmianę hasła'), refresh()),
    onError: notifyError,
  });
  const pkReset = useMutation({
    mutationFn: () => api(`/users/${user.id}/passkeys`, { method: 'DELETE' }),
    onSuccess: () => (notifyOk('Usunięto klucze dostępu pracownika'), refresh()),
    onError: notifyError,
  });
  const mfaReset = useMutation({
    mutationFn: () => api(`/users/${user.id}/mfa/reset`, { method: 'POST' }),
    onSuccess: () => (notifyOk('Wyłączono logowanie dwuetapowe — przy następnym logowaniu wystarczy hasło'), refresh()),
    onError: notifyError,
  });
  const save = useMutation({
    mutationFn: () => api(`/users/${user.id}/password`, { method: 'POST', body: { password, mustChangePassword: requireChange } }),
    onSuccess: () => {
      notifyOk(requireChange ? 'Ustawiono nowe hasło — pracownik zmieni je przy kolejnym logowaniu' : 'Ustawiono nowe hasło — dotychczasowe sesje zostały zakończone');
      setPassword('');
      refresh();
    },
    onError: notifyError,
  });
  return (
    <SectionCard title="Hasło i logowanie dwuetapowe">
      <Stack gap="sm">
        {user.mfaEnabled ? (
          <Alert color="blue" icon={<IconShieldCheck />} title="Logowanie dwuetapowe włączone">
            <Text size="sm" mb="xs">
              Jeśli pracownik zgubił telefon z aplikacją uwierzytelniającą i nie ma kodów zapasowych, wyłącz mu logowanie dwuetapowe — skonfiguruje je ponownie po zalogowaniu.
            </Text>
            <Button
              size="xs"
              color="red"
              variant="light"
              loading={mfaReset.isPending}
              onClick={async () => {
                if (await ask(`${user.fullName} skonfiguruje logowanie dwuetapowe ponownie po zalogowaniu.`, { title: 'Wyłączyć logowanie dwuetapowe?', confirm: 'Wyłącz', danger: true })) mfaReset.mutate();
              }}
            >
              Wyłącz logowanie dwuetapowe
            </Button>
          </Alert>
        ) : (
          <Text size="sm" c="dimmed">
            Logowanie dwuetapowe wyłączone — pracownik włącza je sam: menu konta (lewy dolny róg) → „Bezpieczeństwo logowania”.
          </Text>
        )}
        {user.passkeys > 0 && (
          <Alert color="blue" icon={<IconFingerprint />} title={`Klucze dostępu: ${user.passkeys}`}>
            <Text size="sm" mb="xs">
              Pracownik loguje się odciskiem palca / twarzą / PIN-em urządzenia. Przy zgubionym telefonie lub laptopie usuń jego klucze.
            </Text>
            <Button
              size="xs"
              color="red"
              variant="light"
              loading={pkReset.isPending}
              onClick={async () => {
                if (await ask(`Wszystkie klucze dostępu konta ${user.fullName} zostaną usunięte.`, { title: 'Usunąć klucze dostępu?', confirm: 'Usuń', danger: true })) pkReset.mutate();
              }}
            >
              Usuń klucze dostępu
            </Button>
          </Alert>
        )}
        <Switch
          label="Wymagana zmiana hasła przy kolejnym logowaniu"
          description="Po zalogowaniu pracownik najpierw ustawia nowe hasło; po zmianie opcja wyłącza się sama."
          checked={user.mustChangePassword}
          disabled={required.isPending}
          onChange={(e) => required.mutate(e.currentTarget.checked)}
        />
        <Group align="flex-end" wrap="wrap">
          <PasswordInput label="Nowe hasło" description="min. 10 znaków; kończy sesje pracownika" value={password} onChange={(e) => setPassword(e.currentTarget.value)} style={{ flex: '1 1 260px' }} />
          <Button disabled={password.length < 10} loading={save.isPending} onClick={() => save.mutate()}>
            Ustaw hasło
          </Button>
        </Group>
        <Checkbox size="xs" label="Ustawiając hasło, wymagaj jego zmiany przy kolejnym logowaniu" checked={requireChange} onChange={(e) => setRequireChange(e.currentTarget.checked)} />
      </Stack>
    </SectionCard>
  );
}

function AccountTab({ user, roles, me, onDirty, readOnly }: { user: UserDto; roles: RoleDto[]; me: MeDto; onDirty: (d: boolean) => void; readOnly: boolean }) {
  const refresh = useRefresh(user.id);
  const [login, setLogin] = useState(user.login ?? suggestLogin(user.fullName));
  const [roleCodes, setRoleCodes] = useState(user.roleCodes);
  const [password, setPassword] = useState('');
  useEffect(() => {
    setLogin(user.login ?? suggestLogin(user.fullName));
    setRoleCodes(user.roleCodes);
  }, [user]);
  const put = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api<UserDto>(`/users/${user.id}`, { method: 'PUT', body: userBody(user, patch) }),
    onSuccess: (u) => {
      notifyOk(u.hasAccount ? (user.hasAccount ? 'Zapisano konto' : `Założono konto — login: ${u.login}`) : 'Konto wyłączone — pracownik nie może się logować');
      setPassword('');
      refresh();
    },
    onError: notifyError,
  });
  const roleSelect = (
    <MultiSelect label="Role (uprawnienia)" data={roles.map((r) => ({ value: r.code, label: r.name }))} value={roleCodes} onChange={setRoleCodes} />
  );
  const self = me.id === user.id;
  // role wybiera się tu tylko przy zakładaniu konta (potem — zakładka „Dostęp”)
  const dirty = login !== (user.login ?? suggestLogin(user.fullName)) || (!user.hasAccount && [...roleCodes].sort().join() !== [...user.roleCodes].sort().join()) || password !== '';
  useReportDirty(dirty, onDirty);

  if (!user.hasAccount && readOnly)
    return (
      <Alert color="gray" title="Pracownik bez konta" maw={MAW}>
        Nie loguje się do CRM ani do aplikacji. Może być w ekipach i w planie prac.
      </Alert>
    );
  if (!user.hasAccount)
    return (
      <Stack maw={MAW}>
        <Alert color="gray" title="Pracownik bez konta">
          Nie loguje się do CRM ani do aplikacji. Może być w ekipach i w planie prac. Załóż konto, jeśli ma korzystać z systemu.
        </Alert>
        <SectionCard title="Załóż konto użytkownika">
          <Stack gap="sm">
            <Group grow wrap="wrap" align="flex-start">
              <TextInput label="Login" description="z imienia i nazwiska — można zmienić" value={login} onChange={(e) => setLogin(e.currentTarget.value.toLowerCase())} />
              {!user.hasPassword && <PasswordInput label="Hasło początkowe" description="min. 10 znaków" value={password} onChange={(e) => setPassword(e.currentTarget.value)} />}
            </Group>
            {roleSelect}
            <Group justify="flex-end">
              <Button
                loading={put.isPending}
                disabled={!roleCodes.length || (!user.hasPassword && password.length < 10)}
                onClick={() => put.mutate({ hasAccount: true, login, roleCodes, ...(password ? { password } : {}) })}
              >
                Załóż konto
              </Button>
            </Group>
          </Stack>
        </SectionCard>
      </Stack>
    );

  return (
    <Stack maw={MAW}>
      <ReadOnly on={readOnly}>
      <SectionCard
        title="Konto użytkownika"
        actions={
          <Text size="xs" c="dimmed">
            Ostatnie logowanie: {formatDateTime(user.lastLoginAt)}
          </Text>
        }
      >
        <Stack gap="sm">
          <TextInput label="Login" description="Pracownik loguje się loginem (albo e-mailem z karty). Zmianę przekaż pracownikowi." value={login} onChange={(e) => setLogin(e.currentTarget.value.toLowerCase())} maw={320} />
          <Text size="sm" c="dimmed">
            Role, firmy i uprawnienia ustawia się w zakładce „Dostęp”.
          </Text>
          <Group justify="space-between">
            <Button
              size="xs"
              variant="light"
              color="red"
              disabled={self}
              onClick={async () => {
                if (await ask(`${user.fullName} zostanie wylogowany, a telefony pracownika odłączone. Dane pracownika zostają.`, { title: 'Wyłączyć konto?', confirm: 'Wyłącz konto', danger: true }))
                  put.mutate({ hasAccount: false });
              }}
            >
              Wyłącz konto
            </Button>
            <Button loading={put.isPending} disabled={login === user.login} onClick={() => put.mutate({ login })}>
              Zapisz
            </Button>
          </Group>
        </Stack>
      </SectionCard>
      </ReadOnly>
      {!readOnly && <SecurityCard user={user} />}
      <SectionCard title="Telefony z aplikacją mobilną">
        <MobileDevices userId={user.id} canRevoke={can(me, 'urzadzenia_mobilne.uniewaznienie')} />
      </SectionCard>
    </Stack>
  );
}

// ── strona ─────────────────────────────────────────────────────────────────

/** Karta pracownika: dane, uprawnienia zawodowe, funkcje w branżach, konto użytkownika (opcjonalne). */
export function EmployeeCardPage({ me }: { me: MeDto }) {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  // dawne zakładki „Funkcje w branżach” i „Uprawnienia w CRM” → „Dostęp” (stare linki)
  const raw = params.get('zakladka') ?? 'dane';
  const tab = raw === 'funkcje' || raw === 'prawa' ? 'dostep' : raw;
  // niezapisane zmiany w bieżącej zakładce — zakładki nie są trzymane w pamięci, więc przed zmianą pytamy
  const dirty = useRef(false);
  const onDirty = (d: boolean) => {
    dirty.current = d;
  };
  const changeTab = async (v: string | null) => {
    if (!v || v === tab) return;
    if (dirty.current && !(await ask('Zmiany w tej zakładce nie zostały zapisane i zostaną utracone.', { title: 'Przejść bez zapisywania?', confirm: 'Przejdź' }))) return;
    dirty.current = false;
    setParams({ zakladka: v }, { replace: true });
  };
  const users = useQuery({ queryKey: ['users'], queryFn: () => api<UserDto[]>('/users') });
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api<RoleDto[]>('/roles') });
  const companies = useQuery({ queryKey: ['companies'], queryFn: () => api<CompanyDto[]>('/companies') });
  const profile = useQuery({ queryKey: ['employee-profile', id], queryFn: () => api<EmployeeProfileDto>(`/users/${id}/profile`), enabled: !!id });
  const user = users.data?.find((u) => u.id === id);
  // pracownicy.podglad bez zarządzania (np. koordynator) — karta do odczytu
  const readOnly = !can(me, 'uzytkownicy.zarzadzanie');
  const expiring = (profile.data?.qualifications ?? []).filter((q) => qualificationState(q.validUntil, todayInWarsaw()) !== 'ok').length;

  return (
    <QueryState isLoading={users.isLoading || profile.isLoading || roles.isLoading || companies.isLoading} error={users.error ?? profile.error ?? roles.error ?? companies.error}>
      {!user || !profile.data || !roles.data || !companies.data ? (
        <>
          <PageHeader crumbs={[{ label: 'Pracownicy', to: '/pracownicy' }]} title="Pracownik" />
          <EmptyState icon={<IconUserQuestion size={24} />} title="Nie znaleziono pracownika" hint="Mógł zostać usunięty albo link jest nieaktualny — wróć do listy pracowników." />
        </>
      ) : (
        <>
          <PageHeader
            crumbs={[{ label: 'Pracownicy', to: '/pracownicy' }, { label: user.fullName }]}
            title={user.fullName}
            badge={
              <Group gap={6}>
                {user.hasAccount ? (
                  <>
                    <Badge variant="light" color="indigo">
                      z kontem
                    </Badge>
                    <Text size="sm" ff="monospace">
                      {user.login}
                    </Text>
                  </>
                ) : (
                  <Badge variant="outline" color="gray">
                    bez konta
                  </Badge>
                )}
                {!user.active && <Badge color="gray">nieaktywny</Badge>}
                {user.isSubcontractor && (
                  <Badge color="grape" variant="light">
                    podwykonawca
                  </Badge>
                )}
              </Group>
            }
            description={[profile.data.position, user.phone, user.email].filter(Boolean).join(' · ') || undefined}
          />
          <Tabs value={tab} onChange={(v) => void changeTab(v)} keepMounted={false}>
            {readOnly && (
              <Alert color="gray" variant="light" mb="md" maw={MAW}>
                Tylko podgląd — zmiany w danych, kontach i uprawnieniach pracowników wprowadza osoba z prawem zarządzania pracownikami.
              </Alert>
            )}
            <Tabs.List mb="md">
              <Tabs.Tab value="dane">Dane pracownika</Tabs.Tab>
              <Tabs.Tab value="uprawnienia" rightSection={expiring ? <Badge size="xs" color="orange">{expiring}</Badge> : undefined}>
                Kwalifikacje
              </Tabs.Tab>
              <Tabs.Tab value="dostep">Dostęp</Tabs.Tab>
              <Tabs.Tab value="urlopy">Akceptacja urlopów</Tabs.Tab>
              <Tabs.Tab value="konto">Konto i logowanie</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="dane">
              <ReadOnly on={readOnly}>
                <DataTab user={user} profile={profile.data} me={me} onDirty={onDirty} />
              </ReadOnly>
            </Tabs.Panel>
            <Tabs.Panel value="uprawnienia">
              <ReadOnly on={readOnly}>
                <QualificationsTab user={user} profile={profile.data} onDirty={onDirty} />
              </ReadOnly>
            </Tabs.Panel>
            <Tabs.Panel value="dostep">
              <ReadOnly on={readOnly}>
                <AccessTab user={user} roles={roles.data} companies={companies.data} me={me} readOnly={readOnly} onDirty={onDirty} maw={MAW} />
              </ReadOnly>
            </Tabs.Panel>
            <Tabs.Panel value="urlopy">
              <AbsenceLadderTab user={user} users={users.data ?? []} readOnly={readOnly} onDirty={onDirty} maw={MAW} />
            </Tabs.Panel>
            <Tabs.Panel value="konto">
              <AccountTab user={user} roles={roles.data} me={me} onDirty={onDirty} readOnly={readOnly} />
            </Tabs.Panel>

          </Tabs>
        </>
      )}
    </QueryState>
  );
}
